import { formatText, type Label, type Locale } from "../texts";

// Datos y reglas de las pantallas de Empresas y Personas (R3), sin React: se prueban en orgs.test.tsx.

export type OrgRow = { organizationId: number; code: string; name: string; status: string };

export type PeopleSummary = {
  total: number;
  active: number;
  blocked: number;
  inactive: number;
  lastLoginAt: string | null;
  owner: {
    userId: number;
    displayName: string;
    email: string;
    lastLoginAt: string | null;
    ownerSince: string | null;
  } | null;
};

export type OrgDetail = {
  organizationId: number;
  code: string;
  legalName: string;
  commercialName: string | null;
  status: string;
  countryCode: string | null;
  defaultLanguageCode: string | null;
  defaultCurrencyCode: string | null;
  timezone: string;
  /** null: Identity no respondió (H-3, nunca 0). */
  people: PeopleSummary | null;
  inboxes: { total: number; active: number } | null;
  channels: { total: number; active: number } | null;
  canImpersonate: boolean;
};

export type Country = {
  code: string;
  name: string;
  currencyCode: string;
  locale: string;
  timezones: { id: string; label: string }[];
};

/** Estado de la empresa (§5.2): Activa en texto, Suspendida ocre, En alta neutra; cualquier otro, Inactiva. */
export function orgStatus(L: Label, status: string): { text: string; tone: "" | "ochre" | "neutral" } {
  if (status === "ACTIVE") return { text: L("ADM_ORGS_ACTIVE"), tone: "" };
  if (status === "SUSPENDED") return { text: L("ADM_ORGS_SUSPENDED"), tone: "ochre" };
  if (status === "ONBOARDING") return { text: L("ADM_ORGS_ONBOARDING"), tone: "neutral" };
  return { text: L("ADM_ORGS_INACTIVE"), tone: "neutral" };
}

/** «Lima (UTC−05:00)»: la ciudad y la diferencia de hoy, con el signo menos tipográfico. */
export function timezoneLabel(zone: string, now = new Date()): string {
  const city = zone.split("/").pop()!.replace(/_/g, " ");
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longOffset" }).formatToParts(now);
    const offset = parts.find((part) => part.type === "timeZoneName")?.value.replace("GMT", "") || "+00:00";
    return `${city} (UTC${offset.replace("-", "−")})`;
  } catch {
    return zone;
  }
}

/** Nombre que se muestra de la empresa: el comercial si lo tiene. */
export const displayName = (org: Pick<OrgDetail, "commercialName" | "legalName">) =>
  org.commercialName?.trim() || org.legalName;

/** Un nombre de catálogo por su clave (COUNTRY_PE, CUR_PEN); sin clave conocida, el valor del servidor. */
export const known = (L: Label, key: string, fallback: string) => {
  const text = L(key);
  return text.startsWith("⟦") ? fallback : text;
};

export const countryName = (L: Label, code: string | null, fallback?: string) =>
  code ? known(L, `COUNTRY_${code.toUpperCase()}`, fallback ?? code) : "—";

export const currencyName = (L: Label, code: string | null) =>
  code ? known(L, `CUR_${code.toUpperCase()}`, code) : "—";

export const languageName = (L: Label, code: string | null) =>
  !code ? "—" : known(L, `LANG_${code.toUpperCase().replace("-", "_")}`, code);

/**
 * D17 · al elegir un país se ponen su primera zona, su moneda y su idioma; si se vuelve a cambiar de país, otra vez los
 * del país nuevo (lo que la persona cambió a mano antes se reemplaza).
 */
export function regionFor(country: Country | undefined) {
  return {
    countryCode: country?.code ?? "",
    timezone: country?.timezones[0]?.id ?? "",
    currencyCode: country?.currencyCode ?? "",
    locale: country?.locale ?? "",
  };
}

// ---- Personas ----

export type PersonStatus = "active" | "blocked" | "inactive";
export type OrgRole = "ORG_OWNER" | "ORG_ADMIN" | "ORG_MEMBER";

export type MemberRow = {
  userId: number;
  name: string;
  email: string;
  loginName: string | null;
  status: PersonStatus;
  role: OrgRole;
  isOwner: boolean;
  lastLoginAt: string | null;
  modules: string[];
  /** Identity: su rol ya le da todos los módulos encendidos (la celda lo dice en vez de lo concedido uno por uno). */
  modulesByRole?: boolean;
  /** null: Messaging no respondió (la celda dice «—» y un aviso lo explica). */
  inboxes: { inboxId: number; name: string; role: string }[] | null;
};

export const roleKey = (role: string) =>
  role === "ORG_OWNER" ? "ADM_PPL_ROLE_OWNER" : role === "ORG_MEMBER" ? "ADM_PPL_ROLE_MEMBER" : "ADM_PPL_ROLE_ADMIN";

export const statusKey = (status: string) =>
  status === "blocked"
    ? "ADM_PPL_STATUS_BLOCKED"
    : status === "inactive"
      ? "ADM_PPL_STATUS_INACTIVE"
      : "ADM_PPL_STATUS_ACTIVE";

/** Nombre de un módulo: el del catálogo de la empresa si viene, si no por su código (nunca el código crudo, D7). */
export function moduleName(L: Label, code: string, names?: Record<string, string>): string {
  const fixed: Record<string, string> = {
    CRM: "ADM_MODULE_CRM",
    ERP: "ADM_MODULE_ERP",
    EMPLOYEES_IA: "ADM_MODULE_EMP",
    CHAT: "ADM_PPL_MODULE_CHAT",
  };
  if (fixed[code]) return L(fixed[code]);
  return names?.[code] ?? L("USAGE_UNKNOWN");
}

/** Iniciales para la marca de una persona (C17): «Carla Vega» → «CV». */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]!.charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : "";
  return (first + last).toUpperCase();
}

/** Lista corta en una celda: «Chat, CRM, Empleados IA» (la celda corta con desvanecido; todo va en la ficha, E9). */
export const joinList = (items: string[]) => items.join(", ");

/**
 * Por qué alguien entra a todos los módulos sin concederlos (Identity decide qué roles; esto solo elige la frase):
 * la del propietario, la del administrador o, si Identity agregara otro rol, una frase sin nombrarlo.
 */
export const modulesByRoleKey = (role: string) =>
  role === "ORG_OWNER"
    ? "ADM_PPL_MODULES_BY_ROLE_OWNER"
    : role === "ORG_ADMIN"
      ? "ADM_PPL_MODULES_BY_ROLE_ADMIN"
      : "ADM_PPL_MODULES_BY_ROLE_OTHER";

/** Qué pasa con alguien sin bandeja, según su rol en la empresa (las reglas de acceso del Chat, en una frase). */
export const noInboxKey = (role: string) =>
  role === "ORG_OWNER"
    ? "ADM_PPL_INBOX_HELP_OWNER"
    : role === "ORG_ADMIN"
      ? "ADM_PPL_INBOX_HELP_ADMIN"
      : "ADM_PPL_INBOX_HELP_MEMBER";

/** Pestañas de la ficha del usuario, en este orden, con su valor en la dirección (`tab=`). */
export const userTabs = ["resumen", "acceso", "seguridad", "modulos", "bandejas", "actividad"] as const;
export type UserTab = (typeof userTabs)[number];

export const normalizeTab = (value: string | null): UserTab =>
  (userTabs as readonly string[]).includes(value ?? "") ? (value as UserTab) : "resumen";

/** Dirección de la ficha de una persona; una persona bloqueada abre en Seguridad (§5.4). */
export function userHref(orgId: number, userId: number, status?: PersonStatus, tab?: UserTab): string {
  const target = tab ?? (status === "blocked" ? "seguridad" : "resumen");
  return `/Organizations/Usuario?id=${orgId}&userId=${userId}&tab=${target}`;
}

/** El filtro de la lista de personas que se guarda para volver con ← al mismo lugar (E84). */
export const peopleListKey = (orgId: number) => `bheli-admin-people-list:${orgId}`;

/** Lo que pasó en el historial de seguridad, en palabras (D7); una acción nueva sale como «Cambio de seguridad». */
export function eventText(L: Label, action: string): string {
  const key = `ADM_PPL_EVT_${action}`;
  const text = L(key);
  if (!text.startsWith("⟦")) return text;
  const legacy = L(`EVT_${action}`);
  return legacy.startsWith("⟦") ? L("ADM_PPL_EVT_OTHER") : legacy;
}

/** Resultado de un intento de ingreso en palabras. */
export function attemptText(L: Label, success: boolean, reason: string | null): { text: string; failed: boolean } {
  if (success) return { text: L("ADM_PPL_ATTEMPT_OK"), failed: false };
  switch (reason) {
    case "INVALID_CREDENTIALS":
    case "INVALID_PASSWORD":
      return { text: L("ADM_PPL_ATTEMPT_WRONG_PASSWORD"), failed: true };
    case "LOCKED":
    case "ACCOUNT_LOCKED":
      return { text: L("ADM_PPL_ATTEMPT_LOCKED"), failed: true };
    case "MFA_FAILED":
    case "MFA_REQUIRED":
      return { text: L("ADM_PPL_ATTEMPT_MFA"), failed: true };
    default:
      return { text: L("ADM_PPL_ATTEMPT_FAILED"), failed: true };
  }
}

/** Política de la empresa como dato de solo lectura: «Contraseñas cada 90 días · Verificación obligatoria para administradores». */
export function policyText(
  L: Label,
  expiration: string | null | undefined,
  mfaMode: string | null | undefined,
): string {
  if (!expiration && !mfaMode) return "—";
  const days = { "30_DAYS": 30, "60_DAYS": 60, "90_DAYS": 90, "180_DAYS": 180 }[expiration ?? ""] as number | undefined;
  const password = days
    ? formatText(L("ADM_PPL_POLICY_DAYS"), days)
    : expiration === "CUSTOM"
      ? L("ADM_PPL_POLICY_CUSTOM")
      : L("ADM_PPL_POLICY_NEVER");
  const mfa =
    mfaMode === "REQUIRED_ALL"
      ? L("ADM_PPL_POLICY_MFA_ALL")
      : mfaMode === "REQUIRED_PRIVILEGED"
        ? L("ADM_PPL_POLICY_MFA_ADMINS")
        : L("ADM_PPL_POLICY_MFA_OPTIONAL");
  return `${password} · ${mfa}`;
}

/** Día de una fecha en una zona (aaaa-mm-dd). */
export const dayIn = (date: Date, zone: string) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);

/**
 * D12 · C24 1.14.8 · el único formateador de fechas del Admin (AR7): «Hoy 09:42», «Ayer 18:20», «Lunes 11:03» (dentro de
 * los 7 días), después «3 de setiembre» —«3 de setiembre, 11:03» si la columna lleva la hora (`withTime`)— y el año solo
 * si no es el actual. En la zona de la empresa o, fuera de una, la de Lima. `inline` las deja en minúscula para el medio
 * de una frase.
 */
export function whenIn(
  L: Label,
  locale: Locale,
  iso: string | null | undefined,
  zone: string,
  inline = false,
  withTime = false,
): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const tz = safeZone(zone);
  const time = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: tz,
  }).format(date);
  const now = new Date();
  const day = dayIn(date, tz);
  if (day === dayIn(now, tz)) return formatText(L(inline ? "ADM_AI_TODAY_AT_INLINE" : "ADM_AI_TODAY_AT"), time);
  if (day === dayIn(new Date(now.getTime() - 86400000), tz))
    return formatText(L(inline ? "ADM_AI_YESTERDAY_AT_INLINE" : "ADM_AI_YESTERDAY_AT"), time);
  const days = (Date.parse(dayIn(now, tz)) - Date.parse(day)) / 86400000;
  if (days > 1 && days < 7) {
    const weekday = new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: tz }).format(date);
    const text = `${weekday} ${time}`;
    return inline ? text : text.charAt(0).toUpperCase() + text.slice(1);
  }
  const date$ = dateIn(locale, iso, zone, day.slice(0, 4) !== dayIn(now, tz).slice(0, 4));
  return withTime ? `${date$}, ${time}` : date$;
}

/** D12 · fecha completa en la zona de la empresa: «6 de noviembre» o «12 de marzo de 2026» (con año si se pide o no es este). */
export function dateIn(locale: Locale, iso: string | null | undefined, zone: string, withYear = true): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const text = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: safeZone(zone),
  }).format(date);
  // C24 · en es-PE el mes 9 es «setiembre».
  return locale === "es-PE" ? text.replace("septiembre", "setiembre") : text;
}

function safeZone(zone: string): string {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone;
  } catch {
    return "America/Lima";
  }
}

/**
 * C20 · AR23 · el tono de una persona (0–330, de 30 en 30), el mismo cálculo del chat del portal (avatarTone): por su id
 * o, sin id, por su nombre. Las iniciales y el nombre de quien escribe usan este matiz (claro y oscuro en el CSS).
 */
export function personHue(seed: number | string | null | undefined): number {
  let id = typeof seed === "number" ? seed : 0;
  if (typeof seed === "string") for (const character of seed) id = (id * 31 + character.charCodeAt(0)) % 100003;
  return Math.trunc((((Math.abs(id) % 360) * 137 + 73) % 360) / 30) * 30;
}

/** «1 usuario» / «18 usuarios» con el formateador único (E18, E87). */
export const plural = (L: Label, locale: Locale, count: number, one: string, many: string) =>
  formatText(L(count === 1 ? one : many), count.toLocaleString(locale));
